import {
  ComparatorContext,
  ProductType,
  useField,
} from '@kizi/comparator-core';
import { Button, Div, P, Span } from '@kizi/core-components';
import { Anchor, IconV2 as Icon } from '@kizi/shared-components';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { isEmailValid } from '../../utils/global';
import { Window } from '../Window';
import { BasicInput } from '../wrappers/inputs/withFormProps';
import {
  ModalWithAccordion,
  ModalWithAccordionProps,
} from './ModalWithAccordion';
export interface InnerModalContent {
  inputLabel: string;
  submitButton: string;
  text: string;
  textAfterPrivacyModal: string;
  textBeforePrivacyModal: string;
}
export interface MailMeThisOverviewProps {
  buttonLabel: string;
  buttonIcon?: string;
  privacyModal: ModalWithAccordionProps | null;
  title: string;
  innerModal: InnerModalContent;
  requestLogIdName: string;
  requestEndpoint: { url: string; options: RequestInit };
  privacyUrl?: string;
  showIcon?: boolean;
}
export interface InnerModalProps {
  privacyModal: ModalWithAccordionProps | null;
  innerModal: InnerModalContent;
  requestLogIdName: string;
  requestEndpoint: { url: string; options: RequestInit };
  privacyUrl?: string;
  showIcon?: boolean;
}

export const InnerModal = ({
  privacyModal,
  innerModal,
  requestLogIdName,
  privacyUrl,
  showIcon = false,
}: InnerModalProps) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(false);
  const [result, setResult] = useState(false);
  const [apiError, setApiError] = useState(false);
  const requestLogId = useField(requestLogIdName);
  const { api, name } = useContext(ComparatorContext);

  const handleSubmit = async () => {
    try {
      await api.sendEmailToCustomer(
        requestLogId?.value || '',
        email,
        ProductType[name.toUpperCase()],
      );
      setResult(true);
      if (name.startsWith('os-', 0)) {
        const emailEvent = {
          event: 'email_comparisons',
          event_action: 'Email details',
          event_label: 'E-mail dit overzicht',
          event_category: `Funnel - ${name.substr(3)}`,
          comparison_id: requestLogId?.value,
        };
        window?.dataLayer?.push(emailEvent);
      }
    } catch (e) {
      setApiError(true);
    }
  };

  const validateEmail = () => {
    setError(email.length > 0 && !isEmailValid(email));
  };

  useEffect(() => {
    if (error) {
      setError(false);
    }
  }, [email]);

  const {
    inputLabel,
    submitButton,
    text,
    textAfterPrivacyModal,
    textBeforePrivacyModal,
  } = innerModal;

  const apiResponseMessage = !apiError ? (
    <P variant="bodyRegular">Bedankt! We hebben je een e-mail gestuurd.</P>
  ) : (
    <P variant="bodyRegular">
      We kunnen je verzoek niet verwerken, probeer het later nog een keer
    </P>
  );

  const emailErrorMessage = error
    ? 'U heeft een niet-bestaand e-mailadres ingevuld.'
    : undefined;

  const isDisabled = email.length === 0 || error ? 'disabled' : '';

  const form = !result ? (
    <>
      <BasicInput
        _label={inputLabel}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={validateEmail}
        inputMode="email"
        type="email"
        _error={emailErrorMessage}
        name="email"
      />

      <Div mt={2} display="flex" justifyContent="flex-end">
        <Button
          onClick={handleSubmit}
          variant="primary"
          className={isDisabled}
          disabled={email.length === 0 || error}
        >
          {submitButton}
        </Button>
      </Div>
    </>
  ) : (
    apiResponseMessage
  );

  if (!requestLogId) return null;

  const modalOrLink = () => {
    if (privacyModal) {
      return (
        <Span display="inline-block">
          <ModalWithAccordion
            showIcon={showIcon}
            buttonProps={{ mx: 1 }}
            {...privacyModal}
          />
        </Span>
      );
    } else {
      return (
        <Anchor href={privacyUrl ?? ''} variant="anchorRegular">
          {' '}
          privacyverklaring{' '}
        </Anchor>
      );
    }
  };

  return (
    <>
      <Div variant="modalHeader">
        <P variant="bodyRegular">{text}</P>

        <P variant="bodyRegular">
          {textBeforePrivacyModal}
          {modalOrLink()}
          {textAfterPrivacyModal}
        </P>
      </Div>

      <Div mt={6} pb={[null, 6]}>
        {form}
      </Div>
    </>
  );
};

export const MailMeThisOverview: React.FC<MailMeThisOverviewProps> =
  React.forwardRef<HTMLDivElement, MailMeThisOverviewProps>(
    (
      {
        buttonIcon,
        buttonLabel,
        privacyModal,
        title,
        innerModal,
        requestLogIdName,
        requestEndpoint,
        privacyUrl = '',
        showIcon = false,
      },
      ref,
    ) => {
      const { name } = useContext(ComparatorContext);
      const isOs = name.includes('os-');

      const [show, setShow] = useState(false);
      const buttonRef = useRef<HTMLButtonElement>(null);

      return (
        <Div variant="filterSorting">
          <Span
            role="button"
            onClick={() => setShow(!show)}
            ref={buttonRef}
            style={{ cursor: 'pointer' }}
            variant={isOs ? 'iconSortingTextItem' : 'iconTextItem'}
            display="flex"
            alignItems="center"
          >
            {buttonIcon && (
              <Icon name={buttonIcon} size={isOs ? 24 : 22} mr={2} />
            )}
            {buttonLabel}
          </Span>

          <Window
            showIcon={showIcon}
            onClose={() => setShow(false)}
            modalProps={{
              show: show,
              title,
              children: (
                <Div ref={ref}>
                  <InnerModal
                    privacyModal={privacyModal}
                    innerModal={innerModal}
                    requestLogIdName={requestLogIdName}
                    requestEndpoint={requestEndpoint}
                    privacyUrl={privacyUrl}
                  />
                </Div>
              ),
            }}
            show={show}
            title={title}
            type="modal"
          >
            <InnerModal
              showIcon={showIcon}
              privacyModal={privacyModal}
              innerModal={innerModal}
              requestLogIdName={requestLogIdName}
              requestEndpoint={requestEndpoint}
            />
          </Window>
        </Div>
      );
    },
  );
